import { Card, Progress, Typography, Space, Tag, Button } from 'antd'
import type { ReactNode } from 'react'

const { Title, Text } = Typography

interface StatusCardProps {
  title: string
  status: {
    label: string
    color?: string
    icon?: ReactNode
  }
  progress?: {
    percent: number
    status?: 'success' | 'exception' | 'normal' | 'active'
  }
  description?: string
  metadata?: Array<{
    label: string
    value: string | ReactNode
  }>
  action?: {
    label: string
    onClick: () => void
    icon?: ReactNode
    type?: 'primary' | 'default' | 'dashed' | 'link'
  }
  loading?: boolean
  style?: React.CSSProperties
}

/**
 * Status Card Component
 *
 * Displays entity status (workflow, appointment, etc.) with progress indicator.
 * Follows the patient portal's ServiceBanner pattern but adapted for admin.
 *
 * Usage:
 * ```tsx
 * <StatusCard
 *   title="TRT Patient Journey"
 *   status={{ label: 'In Progress', color: 'processing' }}
 *   progress={{ percent: 65, status: 'active' }}
 *   description="Step 3/5: Awaiting lab results"
 *   metadata={[
 *     { label: 'Started', value: '2 weeks ago' },
 *     { label: 'Doctor', value: 'Dr. Smith' }
 *   ]}
 *   action={{
 *     label: 'View Details',
 *     onClick: () => {}
 *   }}
 * />
 * ```
 */
export function StatusCard({
  title,
  status,
  progress,
  description,
  metadata,
  action,
  loading,
  style,
}: StatusCardProps) {
  return (
    <Card loading={loading} style={{ height: '100%', ...style }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Header with status badge */}
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>
            {title}
          </Title>
          <Tag color={status.color} icon={status.icon}>
            {status.label}
          </Tag>
        </Space>

        {/* Progress bar */}
        {progress && (
          <Progress
            percent={progress.percent}
            status={progress.status}
            strokeColor={progress.status === 'active' ? '#667EEA' : undefined}
          />
        )}

        {/* Description */}
        {description && (
          <Text type="secondary" style={{ fontSize: 14 }}>
            {description}
          </Text>
        )}

        {/* Metadata */}
        {metadata && metadata.length > 0 && (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {metadata.map((item, index) => (
              <Space key={index} style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {item.label}:
                </Text>
                <Text strong style={{ fontSize: 13 }}>
                  {item.value}
                </Text>
              </Space>
            ))}
          </Space>
        )}

        {/* Action button */}
        {action && (
          <Button
            type={action.type || 'default'}
            icon={action.icon}
            onClick={action.onClick}
            block
          >
            {action.label}
          </Button>
        )}
      </Space>
    </Card>
  )
}
